import React, { useMemo, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import DataTable from "examples/Tables/DataTable";

import config from "../../../../config";
import useAccidentsTable from "./useAccidentsTable";

const AccidentsTable = ({ setErrorSB, setSuccessSB }) => {
  const navigate = useNavigate();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [counter, setCounter] = useState(0);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const { tableData, loading, error } = useAccidentsTable(counter);

  const openActions = (row) => {
    setSelectedRow(row);
    setActionsOpen(true);
  };

  const closeActions = () => {
    setActionsOpen(false);
    setSelectedRow(null);
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
  };

  const handleDeleteConfirmed = async () => {
    if (!selectedRow) return;

    try {
      await axios.delete(config.MAP_MATCHING_API_URL + `/accidents/${selectedRow.id}`, {
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": `${config.MAP_MATCHING_API_TOKEN}`,
        },
      });

      setSuccessSB(true);
      setCounter((currentCounter) => currentCounter + 1);
    } catch (deleteError) {
      setErrorSB(true);
    } finally {
      setOpenDeleteDialog(false);
      setActionsOpen(false);
      setSelectedRow(null);
    }
  };

  const dialogPaperSx = (theme) => ({
    backgroundColor: darkMode ? theme.palette.background.card : theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderRadius: 2,
    minWidth: 420,
    border: `1px solid ${
      darkMode ? "rgba(255,255,255,0.12)" : "rgba(52,71,103,0.12)"
    }`,
    boxShadow: theme.shadows[darkMode ? 24 : 8],
  });

  const dialogDividerColor = darkMode ? "rgba(255,255,255,0.12)" : "rgba(52,71,103,0.12)";

  const tableWithActions = useMemo(() => {
    if (!tableData) return tableData;

    return {
      ...tableData,
      rows: tableData.rows.map((row) => ({
        ...row,
        actions: (
          <IconButton
            size="small"
            aria-label="row actions"
            onClick={(event) => {
              event.stopPropagation();
              openActions(row);
            }}
            sx={(theme) => ({
              color: darkMode ? theme.palette.common.white : theme.palette.text.primary,
              "&:hover": {
                color: darkMode ? theme.palette.common.white : theme.palette.text.primary,
              },
            })}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        ),
      })),
    };
  }, [darkMode, tableData]);

  if (loading) {
    return (
      <div className="rawContainerCenteredH">
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <MDBox p={3}>
        <MDTypography variant="h6" color="error" gutterBottom>
          Error: {error}
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <div className="diagramContainer">
      <div className="scrollable-100">
        <DataTable table={tableWithActions} canSearch onRowClick={(row) => openActions(row)} />
      </div>

      <Dialog
        open={actionsOpen}
        onClose={closeActions}
        PaperProps={{
          sx: dialogPaperSx,
        }}
      >
        <DialogTitle>Ενέργειες</DialogTitle>

        <DialogContent>
          <MDTypography variant="button" color="text">
            Επιλέξτε ενέργεια για το ατύχημα #{selectedRow?.id}
          </MDTypography>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <MDButton
            variant="outlined"
            color="info"
            onClick={() => {
              if (!selectedRow) return;
              navigate(`/list/accidents/${selectedRow.id}`);
              closeActions();
            }}
            sx={{ textTransform: "none" }}
          >
            Προβολή
          </MDButton>

          <MDButton
            variant="contained"
            color="info"
            onClick={() => {
              if (!selectedRow) return;
              navigate(`/list/accidents/${selectedRow.id}/edit`);
              closeActions();
            }}
            sx={{ textTransform: "none" }}
          >
            Επεξεργασία
          </MDButton>

          <MDButton
            variant="contained"
            color="error"
            onClick={() => {
              setActionsOpen(false);
              setOpenDeleteDialog(true);
            }}
            sx={{ textTransform: "none" }}
          >
            Διαγραφή
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: dialogPaperSx,
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${dialogDividerColor}`,
          }}
        >
          Επιβεβαίωση Διαγραφής
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          <MDTypography color="text">
            Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το ατύχημα;
          </MDTypography>
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: `1px solid ${dialogDividerColor}`,
            px: 3,
            py: 2,
          }}
        >
          <MDButton
            onClick={handleDeleteCancel}
            variant="outlined"
            color="secondary"
            sx={(theme) => ({
              color: theme.palette.text.primary,
              borderColor: darkMode ? "rgba(255,255,255,0.35)" : "rgba(52,71,103,0.3)",
              textTransform: "none",
              "&:hover": {
                borderColor: theme.palette.text.primary,
              },
            })}
          >
            Ακύρωση
          </MDButton>

          <MDButton
            onClick={handleDeleteConfirmed}
            color="error"
            variant="contained"
            sx={{
              textTransform: "capitalize",
            }}
          >
            Διαγραφή
          </MDButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AccidentsTable;
